import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import type { Bundesland } from '@chancenwerk/enums/enums.js';
import { DateTime } from 'luxon';
import { Partner } from '../../master-data-management/partner/entities/Partner.js';
import { mongoExistsAndNotNull, mongoNotExistsOrNull } from '../../utils/mongo.js';
import type { WorkshopAttendanceStatus } from './WorkshopAttendanceStatus.js';

export enum WorkshopFilterCondition {
  MORE_THAN_8_WEEKS = 'Länger als 8 Wochen NICHT durchgeführt',
  TOOK_LONGER_THAN_8_WEEKS = 'Länger als 8 Wochen für Durchführung benötigt',
  IN_8_WEEKS = 'Innerhalb von 8 Wochen durchgeführt',
  MULTIPLE_TIMES = 'Mehrere Durchführungen',
}

export function applyWorkshopFilterCondition(cond: WorkshopFilterCondition, filter: WorkshopFilter) {
  filter.minDaysSinceTraining = null;
  filter.maxDaysSinceTraining = null;
  filter.minDaysBetweenWorkshop = null;
  filter.maxDaysBetweenWorkshop = null;
  filter.hadWorkshop = null;
  filter.condition = cond;

  switch (cond) {
    case WorkshopFilterCondition.IN_8_WEEKS:
      filter.minDaysBetweenWorkshop = 0;
      filter.maxDaysBetweenWorkshop = 56;
      break;
    case WorkshopFilterCondition.MORE_THAN_8_WEEKS:
      filter.minDaysSinceTraining = 57;
      filter.maxDaysSinceTraining = null;
      filter.hadWorkshop = false;
      break;
    case WorkshopFilterCondition.TOOK_LONGER_THAN_8_WEEKS:
      filter.minDaysBetweenWorkshop = 57;
      filter.maxDaysBetweenWorkshop = null;
      filter.hadWorkshop = true;
      break;
  }
}

@Entity()
export class WorkshopFilter extends AdornisFilter {
  public static override _class = 'WorkshopFilter';

  constructor(doc: PartialEntityData<WorkshopFilter>) {
    super(doc);
  }

  @Field(() => String)
  public bundesland: Maybe<Bundesland>;

  @Field(() => [Partner])
  public partners: Maybe<Partner[]>;

  @Field(() => [String])
  public partnerIDs: Maybe<string[]>;

  @Field(() => String)
  public workshopID: Maybe<string>;

  @Field(() => Boolean)
  public requiresTraining: Maybe<boolean>;

  @Field(() => String)
  public userID: Maybe<string>;

  @Field(() => String)
  public condition: Maybe<WorkshopFilterCondition>;

  @Field(() => Int)
  public minDaysSinceTraining: Maybe<number>;

  @Field(() => Int)
  public maxDaysSinceTraining: Maybe<number>;

  @Field(() => Int)
  public minDaysBetweenWorkshop: Maybe<number>;

  @Field(() => Int)
  public maxDaysBetweenWorkshop: Maybe<number>;

  @Field(() => Boolean)
  public hadWorkshop: Maybe<boolean>;

  @Field(() => DateTime)
  public from: Maybe<DateTime>;

  @Field(() => DateTime)
  public till: Maybe<DateTime>;

  @Field(() => String)
  public state: Maybe<WorkshopAttendanceStatus>;

  @Field(() => Boolean)
  public hasState: Maybe<boolean>;

  get allPartnerIDs() {
    return (this.partnerIDs ?? []).concat((this.partners ?? []).map(partner => partner._id));
  }

  override getMongoFilter(): Record<string, any> {
    let filter: Record<string, any> = {};
    if (this.workshopID) {
      filter.workshopID = this.workshopID;
    }

    if (this.userID) {
      filter.userID = this.userID;
    }

    if (this.partnerIDs && this.partnerIDs.length > 0) {
      filter.partnerID = { $in: this.partnerIDs };
    }

    if (this.partners && this.partners.length > 0) {
      filter.partnerID = { $in: this.partners.map(partner => partner._id) };
    }

    if (this.from && this.till) {
      switch (this.condition) {
        case WorkshopFilterCondition.MORE_THAN_8_WEEKS:
          break;
        case WorkshopFilterCondition.TOOK_LONGER_THAN_8_WEEKS:
          break;
        case WorkshopFilterCondition.IN_8_WEEKS:
          break;
        case WorkshopFilterCondition.MULTIPLE_TIMES:
          break;
        default:
          filter.date = { $gte: this.from.startOf('day').toJSDate(), $lte: this.till.endOf('day').toJSDate() };
          break;
      }
    }

    if (this.state) {
      filter.state = this.state;
    } else if (this.hasState) {
      filter = { ...filter, ...mongoExistsAndNotNull('state') };
    }

    return filter;
  }

  getMongoAggergationFilter(): Record<string, any> {
    let filter: Record<string, any> = {};

    if (this.minDaysSinceTraining && this.maxDaysSinceTraining) {
      filter.daysSinceTraining = { $gte: this.minDaysSinceTraining, $lte: this.maxDaysSinceTraining };
    } else {
      if (this.minDaysSinceTraining) {
        filter.daysSinceTraining = { $gte: this.minDaysSinceTraining };
      }
      if (this.maxDaysSinceTraining) {
        filter.daysSinceTraining = { $lte: this.maxDaysSinceTraining };
      }
    }

    if (this.minDaysBetweenWorkshop && this.maxDaysBetweenWorkshop) {
      filter.daysBetweenWorkshop = { $gte: this.minDaysBetweenWorkshop, $lte: this.maxDaysBetweenWorkshop };
    } else {
      if (this.minDaysBetweenWorkshop) {
        filter.daysBetweenWorkshop = { $gte: this.minDaysBetweenWorkshop };
      }
      if (this.maxDaysBetweenWorkshop) {
        filter.daysBetweenWorkshop = { $lte: this.maxDaysBetweenWorkshop };
      }
    }

    if (this.condition === WorkshopFilterCondition.MULTIPLE_TIMES) {
      filter['allParticipations.1'] = { $exists: true };
    }

    if (this.from || this.till) {
      switch (this.condition) {
        case WorkshopFilterCondition.MORE_THAN_8_WEEKS:
          filter.eightWeeksDate = {
            $gte: this.from ? this.from.startOf('day').toJSDate() : undefined,
            $lte: this.till ? this.till.endOf('day').toJSDate() : undefined,
          };
          break;
        case WorkshopFilterCondition.TOOK_LONGER_THAN_8_WEEKS:
        case WorkshopFilterCondition.IN_8_WEEKS:
          filter['participation.date'] = {
            $gte: this.from ? this.from.startOf('day').toJSDate() : undefined,
            $lte: this.till ? this.till.endOf('day').toJSDate() : undefined,
          };
          break;
        case WorkshopFilterCondition.MULTIPLE_TIMES:
          filter['allParticipations'] = {
            $elemMatch: {
              date: {
                $gte: this.from ? this.from.startOf('day').toJSDate() : undefined,
                $lte: this.till ? this.till.endOf('day').toJSDate() : undefined,
              },
            },
          };
        default:
          break;
      }
    }

    if (this.requiresTraining === true) {
      filter = { ...filter, 'workshop.requiresTraining': true };
    } else if (this.requiresTraining === false) {
      filter = { ...filter, 'workshop.requiresTraining': false };
    }

    if (this.hadWorkshop === true) {
      filter = { ...filter, ...mongoExistsAndNotNull('participation') };
    } else if (this.hadWorkshop === false) {
      filter = { ...filter, ...mongoNotExistsOrNull('participation') };
    }

    return filter;
  }
}
